import { useQueryClient } from "@tanstack/react-query";
import { UppdragQueryKey } from "api/uppdrag.api";
import { BottomSheet } from "components/BottomSheet";
import { ComboBox } from "components/ComboBox";
import { DropdownFormProvider } from "components/DropdownFormProvider";
import { RadioCardGroup } from "components/RadioCardGroup";
import { TextAreaFormProvider } from "components/TextAreaFormProvider";
import { TextInputFormProvider } from "components/TextInputFormProvider";
import { useCreateOrUpdateSeparatorLogic } from "hooks/useCreateOrUpdateSeparatorLogic.hook";
import { AddSeparatorModal } from "pages/app/facility/AddSeparatorModal";
import { FormProvider } from "react-hook-form";
import {
  AvskiljareTomningType,
  getAvskiljareTomningTypeLabel,
} from "types/enums";
import { CreateWasteLogicRetVal, TWasteSchema } from "../useCreateWasteLogic";
import { SheetPrimaryButton, SheetSecondaryButton } from "./SheetActions";

export type WasteWeightField = "weightPreliminary" | "weightFinal";

type Props = {
  logic: CreateWasteLogicRetVal;
  title: string;
  highlightField?: WasteWeightField;
};

const WeightInput = ({
  label,
  name,
  isHighlighted,
}: {
  label: string;
  name: WasteWeightField;
  isHighlighted?: boolean;
}) => {
  if (isHighlighted) {
    return (
      <div className="mb-6 rounded-xl border border-dashed border-blue-300 bg-blue-50/40 p-3">
        <TextInputFormProvider
          label={label}
          name={name}
          appendix="kg"
          customSize="half"
          autoFocus
        />
        <p className="mt-2 text-sm font-semibold text-blue-700">
          Fyll i {label.toLowerCase()}
        </p>
      </div>
    );
  }

  return (
    <TextInputFormProvider
      label={label}
      name={name}
      appendix="kg"
      customSize="half"
      className="mb-6"
    />
  );
};

export const OperatorWasteSheet = ({ logic, title, highlightField }: Props) => {
  const queryClient = useQueryClient();

  const addSeparatorLogic = useCreateOrUpdateSeparatorLogic(
    logic.uppdrag.facility,
    () => queryClient.invalidateQueries({ queryKey: [UppdragQueryKey.GetOne] }),
  );

  return (
    <BottomSheet
      isOpen={logic.isModalOpen}
      onClose={logic.onModalClose}
      afterClose={logic.afterModalClose}
      title={title}
    >
      <FormProvider {...logic.form}>
        <form className="pb-2" onSubmit={logic.onSubmit}>
          {!logic.hideSeparator && (
            <DropdownFormProvider
              items={logic.separatorDropdownItems}
              label="Objekt"
              name="tomningSeparatorUuid"
              action={
                !logic.isSetSeparatorDisabled
                  ? addSeparatorLogic.openModal
                  : undefined
              }
              actionText={
                !logic.isSetSeparatorDisabled ? "Nytt objekt" : undefined
              }
              className="mb-6"
              disabled={logic.isSetSeparatorDisabled}
            />
          )}

          {logic.isAvskiljareSelected && (
            <RadioCardGroup<TWasteSchema>
              name="tomningType"
              label="Typ av tömning"
              required
              items={[
                {
                  id: AvskiljareTomningType.Heltomning,
                  label: getAvskiljareTomningTypeLabel(
                    AvskiljareTomningType.Heltomning,
                  ),
                },
                {
                  id: AvskiljareTomningType.Toppsugning,
                  label: getAvskiljareTomningTypeLabel(
                    AvskiljareTomningType.Toppsugning,
                  ),
                },
              ]}
              radioGroupClassName="grid grid-cols-2 gap-x-4"
              className="mb-6"
            />
          )}

          <ComboBox
            label="Avfallskod*"
            items={logic.wasteCodeComboBoxItems}
            setQuery={logic.setWasteCodeQuery}
            selectedItem={logic.selectedWasteCode}
            setSelectedItem={logic.setSelectedWasteCode}
            errorMessage={logic.form.formState.errors.wasteCodeUuid?.message}
            className="mb-6"
          />

          <WeightInput
            label="Uppskattad vikt"
            name="weightPreliminary"
            isHighlighted={highlightField === "weightPreliminary"}
          />
          <WeightInput
            label="Vägd vikt"
            name="weightFinal"
            isHighlighted={highlightField === "weightFinal"}
          />

          <AddSeparatorModal logic={addSeparatorLogic} />

          <TextAreaFormProvider label="Kommentar" name="comment" rows={2} />

          <div className="mt-6 flex gap-x-3 border-t border-gray-75 pt-4">
            <SheetSecondaryButton onClick={logic.onModalClose} />
            <SheetPrimaryButton
              isLoading={logic.isPending}
              loadingText="Sparar..."
            >
              Spara
            </SheetPrimaryButton>
          </div>
        </form>
      </FormProvider>
    </BottomSheet>
  );
};
